import { ALL_FILTER, GOOGLE, LINKEDIN, META } from "../utils/constants";
import { resolveCompetitorAdsPlatform } from "./competitorAdExternalUrls";

export const COMPETITOR_ADS_FROM = "competitorAds";
export const META_AD_LIBRARY_FROM = COMPETITOR_ADS_FROM;

const MIN_AD_IMAGE_DIMENSION = 150;
const COMPETITOR_ADS_PLATFORMS = new Set([META, GOOGLE, LINKEDIN]);
const COMPETITOR_ADS_STORAGE_PATH_SEGMENT = "/competitorads/";
const COMPETITOR_ADS_CACHE_RESERVED_KEY_FAVORITE = "favorite";
const COMPETITOR_ADS_CACHE_STATUS_READY = "ready";

export const normalizeCompetitorName = (value) => {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[.\-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const isGoogleSimgadUrl = (url) => {
  const lower = String(url || "").toLowerCase();
  return lower.includes("/simgad/") || lower.includes("/archive/simgad/");
};

const isCompetitorAdPersistedStorageUrl = (url) => {
  const lower = String(url || "").toLowerCase();
  if (!lower) return false;

  return (
    lower.includes(".amazonaws.com") &&
    lower.includes(COMPETITOR_ADS_STORAGE_PATH_SEGMENT)
  );
};

const isAcceptedGoogleAdImageUrl = (url) =>
  isGoogleSimgadUrl(url) || isCompetitorAdPersistedStorageUrl(url);

/**
 * Formats active duration for competitor ad cards (hours if < 24h, otherwise days).
 * @returns {{ unit: "hours" | "days", value: number } | null}
 */
export const getMetaAdActiveDurationDisplay = (activeDurationHours) => {
  const hours = Number(activeDurationHours);
  if (!Number.isFinite(hours) || hours < 0) return null;

  if (hours < 24) {
    return { unit: "hours", value: Math.ceil(hours) };
  }

  return { unit: "days", value: Math.ceil(hours / 24) };
};

export const COMPETITOR_AD_STRONG_SIGNAL_MIN_HOURS = 45 * 24;

export const isCompetitorAdStrongSignal = (activeDurationHours) => {
  const hours = Number(activeDurationHours);
  if (!Number.isFinite(hours) || hours < 0) return false;
  return hours >= COMPETITOR_AD_STRONG_SIGNAL_MIN_HOURS;
};

/** Longest active duration first; ads without duration go last. */
export const compareMetaAdActiveDurationDesc = (a, b) => {
  const aHours = Number(a?.activeDurationHours);
  const bHours = Number(b?.activeDurationHours);
  const aFinite = Number.isFinite(aHours);
  const bFinite = Number.isFinite(bHours);

  if (aFinite && bFinite) return bHours - aHours;
  if (aFinite) return -1;
  if (bFinite) return 1;
  return 0;
};

export const isCompetitorAdGalleryItem = (item, from) => {
  const resolvedFrom = from ?? item?.from;
  return (
    resolvedFrom === COMPETITOR_ADS_FROM || resolvedFrom === "metaAdLibrary"
  );
};

export const isMetaAdLibraryItem = isCompetitorAdGalleryItem;

export const isMetaAdLibraryViewOnlyItem = (item) => item?.type === "video";

// LinkedIn Ad Library does not expose active duration; omit requirement for that platform.
const hasRequiredActiveDuration = ({ activeDurationHours, platform }) =>
  platform === LINKEDIN || Number.isFinite(Number(activeDurationHours));

const isValidCompetitorAdImage = (ad, { platform } = {}) => {
  if (
    !ad?.url ||
    !hasRequiredActiveDuration({
      activeDurationHours: ad?.activeDurationHours,
      platform,
    })
  ) {
    return false;
  }

  if (platform === GOOGLE && !isAcceptedGoogleAdImageUrl(ad.url)) {
    return false;
  }

  const width = Number(ad?.width);
  const height = Number(ad?.height);

  if (
    Number.isFinite(width) &&
    width > 0 &&
    Number.isFinite(height) &&
    height > 0
  ) {
    return width >= MIN_AD_IMAGE_DIMENSION && height >= MIN_AD_IMAGE_DIMENSION;
  }

  return true;
};

const isValidCompetitorAdVideo = (ad, { platform } = {}) => {
  return (
    Boolean(ad?.url) &&
    hasRequiredActiveDuration({
      activeDurationHours: ad?.activeDurationHours,
      platform,
    })
  );
};

const isValidCompetitorAdMedia = ({ type, platform, ...ad }) => {
  if (type === "video") {
    return isValidCompetitorAdVideo(ad, { platform });
  }

  return isValidCompetitorAdImage(ad, { platform });
};

const sanitizeAdditionalMedia = (additionalMedia) => {
  if (!Array.isArray(additionalMedia)) return [];

  return additionalMedia
    .map((entry) => {
      const url = typeof entry?.url === "string" ? entry.url.trim() : "";
      if (!url) return null;

      const type = entry?.type === "video" ? "video" : "image";
      const width = Number(entry?.width);
      const height = Number(entry?.height);

      return {
        url,
        type,
        ...(Number.isFinite(width) && width > 0 ? { width } : {}),
        ...(Number.isFinite(height) && height > 0 ? { height } : {}),
      };
    })
    .filter(Boolean);
};

const trimCompetitorAdStringField = (value) =>
  typeof value === "string" ? value.trim() : "";

const getCompetitorAdDimensionFields = (ad) => {
  const width = Number(ad?.width);
  const height = Number(ad?.height);

  if (
    !Number.isFinite(width) ||
    width <= 0 ||
    !Number.isFinite(height) ||
    height <= 0
  ) {
    return {};
  }

  return { width, height };
};

const getCompetitorAdCopyFields = (ad) => {
  const description = trimCompetitorAdStringField(ad?.description);
  const headline = trimCompetitorAdStringField(ad?.headline);
  const headlineSecondary = trimCompetitorAdStringField(ad?.headlineSecondary);
  const cta = trimCompetitorAdStringField(ad?.cta);
  const destinationUrl = trimCompetitorAdStringField(ad?.destinationUrl);
  const additionalMedia = sanitizeAdditionalMedia(ad?.additionalMedia);

  return {
    ...(description ? { description } : {}),
    ...(headline ? { headline } : {}),
    ...(headlineSecondary ? { headlineSecondary } : {}),
    ...(cta ? { cta } : {}),
    ...(destinationUrl ? { destinationUrl } : {}),
    ...(additionalMedia.length > 0 ? { additionalMedia } : {}),
  };
};

const getAdsEntriesFromPageValue = (pageValue) => {
  if (!pageValue) return [];

  if (Array.isArray(pageValue)) {
    return pageValue.map((ad, adIndex) => [
      ad?.libraryID != null && String(ad.libraryID).trim() !== ""
        ? String(ad.libraryID).trim()
        : String(adIndex),
      ad,
    ]);
  }

  if (typeof pageValue !== "object") return [];

  const adsMap =
    pageValue.ads &&
    typeof pageValue.ads === "object" &&
    !Array.isArray(pageValue.ads)
      ? pageValue.ads
      : pageValue;

  return Object.entries(adsMap).filter(
    ([, ad]) => ad && typeof ad === "object" && !Array.isArray(ad)
  );
};

const mapCompetitorAdToGalleryItem = ({
  pageId,
  libraryID: mapKey,
  ad,
  platform,
}) => {
  const mediaUrl = typeof ad?.url === "string" ? ad.url.trim() : "";
  if (!mediaUrl) return null;

  const mediaType = ad?.type === "video" ? "video" : "image";
  const activeDurationHours = Number(ad?.activeDurationHours);
  const hasActiveDurationHours = Number.isFinite(activeDurationHours);
  const width = Number(ad?.width);
  const height = Number(ad?.height);

  const galleryAd = {
    url: mediaUrl,
    type: mediaType,
    ...(hasActiveDurationHours ? { activeDurationHours } : {}),
    width,
    height,
    platform,
  };

  if (!isValidCompetitorAdMedia(galleryAd)) return null;

  const libraryID = String(ad?.libraryID ?? mapKey).trim();
  if (!libraryID) return null;

  const competitor =
    typeof ad?.competitor === "string" ? ad.competitor.trim() : "";
  const hasDimensions =
    Number.isFinite(width) &&
    width > 0 &&
    Number.isFinite(height) &&
    height > 0;

  return {
    id: libraryID,
    url: mediaUrl,
    type: mediaType,
    pageId,
    libraryID,
    platform,
    from: COMPETITOR_ADS_FROM,
    ...(hasDimensions ? { width, height } : {}),
    ...(competitor ? { competitor } : {}),
    ...(hasActiveDurationHours ? { activeDurationHours } : {}),
    ...(ad?.preview ? { preview: ad.preview } : {}),
    ...(ad?.isFavorite === true ? { isFavorite: true } : {}),
    ...(ad?.featureAddonCensored === true
      ? { featureAddonCensored: true }
      : {}),
    ...getCompetitorAdCopyFields(ad),
  };
};

export const isCompetitorAdFavorite = ({
  favoritesMap,
  pageId,
  libraryID,
  item = null,
}) => {
  const resolvedPageId = pageId ?? item?.pageId;
  const resolvedLibraryID = libraryID ?? item?.libraryID ?? item?.id;

  if (favoritesMap != null) {
    return Boolean(favoritesMap?.[resolvedPageId]?.[resolvedLibraryID]);
  }

  return item?.isFavorite === true;
};

export const buildCompetitorAdFavoritePayload = ({ pageId, libraryID, ad }) => {
  const platform = resolveCompetitorAdsPlatform({ pageValue: ad });

  return {
    id: libraryID,
    url: ad.url,
    platform,
    type: ad.type,
    pageId,
    libraryID,
    favoritedAt: Date.now(),
    ...(ad.competitor ? { competitor: ad.competitor } : {}),
    ...(ad.preview ? { preview: ad.preview } : {}),
    ...(Number.isFinite(Number(ad.activeDurationHours))
      ? { activeDurationHours: Number(ad.activeDurationHours) }
      : {}),
    ...getCompetitorAdDimensionFields(ad),
    ...getCompetitorAdCopyFields(ad),
  };
};

export const hasCompetitorAdFavorites = (favoritesByPageId) => {
  if (!favoritesByPageId || typeof favoritesByPageId !== "object") {
    return false;
  }

  return Object.values(favoritesByPageId).some(
    (pageFavorites) =>
      pageFavorites &&
      typeof pageFavorites === "object" &&
      Object.keys(pageFavorites).length > 0
  );
};

export const formatCompetitorFavoritesForGallery = ({ favoritesByPageId }) => {
  if (!favoritesByPageId || typeof favoritesByPageId !== "object") {
    return [];
  }

  const items = [];

  Object.entries(favoritesByPageId).forEach(([pageId, pageFavorites]) => {
    if (!pageFavorites || typeof pageFavorites !== "object") return;

    Object.entries(pageFavorites).forEach(([libraryID, ad]) => {
      const mediaUrl = typeof ad?.url === "string" ? ad.url.trim() : "";
      if (!mediaUrl) return;

      const resolvedLibraryID = String(ad?.libraryID ?? libraryID).trim();
      if (!resolvedLibraryID) return;

      const mediaType = ad?.type === "video" ? "video" : "image";
      const competitor =
        typeof ad?.competitor === "string" ? ad.competitor.trim() : "";
      const activeDurationHours = Number(ad?.activeDurationHours);
      const platform = resolveCompetitorAdsPlatform({ pageValue: ad });

      items.push({
        id: resolvedLibraryID,
        url: mediaUrl,
        type: mediaType,
        pageId,
        libraryID: resolvedLibraryID,
        platform,
        from: COMPETITOR_ADS_FROM,
        isFavorite: true,
        favoritedAt: Number(ad?.favoritedAt) || 0,
        ...(competitor ? { competitor } : {}),
        ...(ad?.preview ? { preview: ad.preview } : {}),
        ...(Number.isFinite(activeDurationHours)
          ? { activeDurationHours }
          : {}),
        ...getCompetitorAdDimensionFields(ad),
        ...getCompetitorAdCopyFields(ad),
      });
    });
  });

  return items.sort((a, b) => (b.favoritedAt ?? 0) - (a.favoritedAt ?? 0));
};

export const filterGalleryItemsByCompetitor = ({
  items,
  competitorFilterValue,
}) => {
  const filterValue = (competitorFilterValue || "").trim();
  if (!filterValue) return items;

  return items.filter(
    (item) => normalizeCompetitorName(item?.competitor) === filterValue
  );
};

/**
 * Drops ads whose platform is outside the addon tier.
 * `allowedPlatforms === null` means the agency is ungated and sees every platform.
 */
export const filterGalleryItemsByAllowedPlatforms = ({
  items,
  allowedPlatforms,
}) => {
  if (!Array.isArray(allowedPlatforms)) return items || [];

  const allowed = new Set(
    allowedPlatforms.map((platform) => String(platform).trim().toLowerCase())
  );

  return (items || []).filter((item) =>
    allowed.has(resolveCompetitorAdsPlatform({ pageValue: item }).toLowerCase())
  );
};

/**
 * Free tier shows the first `visibilityLimit` ads and censors the rest.
 * `visibilityLimit == null` means the tier has no view cap.
 */
export const censorGalleryItemsBeyondVisibility = ({
  items,
  visibilityLimit,
}) => {
  if (visibilityLimit == null || visibilityLimit === "") return items || [];
  const limit = Number(visibilityLimit);
  if (!Number.isFinite(limit) || limit < 0) return items || [];

  return (items || []).map((item, index) => {
    const censored = index >= limit;
    if (Boolean(item?.featureAddonCensored) === censored) return item;
    if (!censored) {
      const next = { ...item };
      delete next.featureAddonCensored;
      return next;
    }
    return { ...item, featureAddonCensored: true };
  });
};

export const filterGalleryItemsByPlatform = ({
  items,
  platformFilterValue,
}) => {
  const filterValue = String(platformFilterValue || ALL_FILTER)
    .trim()
    .toLowerCase();

  if (filterValue === ALL_FILTER) return items;

  return items.filter(
    (item) =>
      resolveCompetitorAdsPlatform({ pageValue: item }).toLowerCase() ===
      filterValue
  );
};

const isCompetitorAdsCachePageReady = (pageValue) => {
  if (!pageValue || typeof pageValue !== "object" || Array.isArray(pageValue)) {
    return false;
  }

  if (
    !pageValue.ads ||
    typeof pageValue.ads !== "object" ||
    Array.isArray(pageValue.ads)
  ) {
    return false;
  }

  const { cacheStatus } = pageValue;

  if (cacheStatus == null || cacheStatus === "") {
    return true;
  }

  return cacheStatus === COMPETITOR_ADS_CACHE_STATUS_READY;
};

const groupCompetitorAdsCacheByPlatform = (cacheSnapshot) => {
  if (
    !cacheSnapshot ||
    typeof cacheSnapshot !== "object" ||
    Array.isArray(cacheSnapshot)
  ) {
    return {};
  }

  const grouped = { [META]: {}, [GOOGLE]: {}, [LINKEDIN]: {} };

  Object.entries(cacheSnapshot).forEach(([pageId, pageValue]) => {
    if (pageId === COMPETITOR_ADS_CACHE_RESERVED_KEY_FAVORITE) {
      return;
    }

    if (!isCompetitorAdsCachePageReady(pageValue)) {
      return;
    }

    const platform = resolveCompetitorAdsPlatform({ pageValue });

    if (!COMPETITOR_ADS_PLATFORMS.has(platform)) {
      return;
    }

    grouped[platform][pageId] = pageValue;
  });

  return grouped;
};

/**
 * Normalizes RTDB cache snapshot into gallery items.
 * Expects `advertisersCompetitors/{advertiserID}` shape keyed by pageId.
 */
export const formatCompetitorAdsCacheForGallery = (cacheSnapshot) => {
  const pagesMap = groupCompetitorAdsCacheByPlatform(cacheSnapshot);
  return formatCompetitorAdsItemsForGallery(pagesMap);
};

/**
 * Flattens platform-grouped page maps into gallery items.
 * Expects `data: { meta?: pages, google?: pages, linkedIn?: pages }`.
 */
export const formatCompetitorAdsItemsForGallery = (pagesMap) => {
  if (!pagesMap || typeof pagesMap !== "object" || Array.isArray(pagesMap)) {
    return [];
  }

  const items = [];

  Object.entries(pagesMap)
    .filter(([platform]) => COMPETITOR_ADS_PLATFORMS.has(platform))
    .forEach(([platform, pageMap]) => {
      if (!pageMap || typeof pageMap !== "object" || Array.isArray(pageMap)) {
        return;
      }

      Object.entries(pageMap).forEach(([pageId, pageValue]) => {
        getAdsEntriesFromPageValue(pageValue).forEach(([libraryID, ad]) => {
          const galleryItem = mapCompetitorAdToGalleryItem({
            pageId,
            libraryID,
            ad,
            platform,
          });

          if (galleryItem) {
            items.push(galleryItem);
          }
        });
      });
    });

  return items.sort(compareMetaAdActiveDurationDesc);
};

export const formatMetaAdLibraryItemsForGallery =
  formatCompetitorAdsItemsForGallery;
